import MuiAccordion, { AccordionProps as MuiAccordionProps } from "@material-ui/core/Accordion";
import MuiAccordionDetails from "@material-ui/core/AccordionDetails";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import { ExpandMore } from "@material-ui/icons";
import { type ReactNode } from "react";
import { Css, Margin, Only, Padding, Xss } from "src/Css";

export type AccordionXss = Xss<Margin | Padding>;
export interface AccordionProps<X extends Only<AccordionXss, X>>
  extends Pick<MuiAccordionProps, "expanded" | "defaultExpanded"> {
  title: string;
  children: ReactNode;
  xss?: X;
  className?: string;
  hideCarrot?: boolean;
}

/** Accordion component which animates height when opened
 * TODO:
 * - Use Framer Motion to remove Mui dependency
 * - Move icon into HOP library
 */
export function Accordion<X extends Only<AccordionXss, X>>(props: AccordionProps<X>) {
  const { title, children, className, hideCarrot = false, xss, ...otherProps } = props;
  const isList = className?.split(/\s+/).includes("list") ?? false;

  return (
    <MuiAccordion
      square
      className={className}
      css={{ ...accordionCss, ...xss }}
      classes={{ expanded: accordionExpandedClassName }}
      {...otherProps}
    >
      <MuiAccordionSummary
        className={className}
        css={accordionSummaryCss}
        classes={{
          content: isList ? accordionSummaryListContentClassName : accordionSummaryContentClassName,
          expandIcon: accordionSummaryExpandIconClassName,
          expanded: accordionSummaryExpandedClassName,
        }}
        expandIcon={<ExpandMore css={Css.if(hideCarrot).dn.$} />}
      >
        {title}
      </MuiAccordionSummary>
      <MuiAccordionDetails css={accordionDetailsCss}>{children}</MuiAccordionDetails>
    </MuiAccordion>
  );
}

const accordionCss = {
  ...Css.p2.m0.bgTransparent.shadowNone.bt.bcDarkTransparentGray.$,
  ...Css.when(":last-child").bb.bcDarkTransparentGray.$,
  ...Css.element("::before").add("backgroundColor", "unset").$,
};

const accordionExpandedClassName = Css.props(Css.m0.bt.bcDarkTransparentGray.add("margin", "0 !important").$)
  .className as string;

const accordionSummaryCss = Css.p0.mh0.$;

const accordionSummaryContentClassName = Css.props(Css.m0.t16.$).className as string;

const accordionSummaryListContentClassName = Css.props(Css.m0.t12up.gray600.$).className as string;

const accordionSummaryExpandIconClassName = Css.props(Css.p0.mr0.$).className as string;

const accordionSummaryExpandedClassName = Css.props(
  Css.mh0.m0.add("minHeight", "0 !important").add("margin", "0 !important").$,
).className as string;

const accordionDetailsCss = Css.mt3.p0.$;
